<template>
  <v-dialog v-model="dialog" persistent max-width="700px" content-class="termination-dialog">
    <v-card dark style="border-radius: 28px; background-color: #1e1e1e">
      <v-btn fab absolute top right small class="mt-4 mr-4" color="#333" @click="closeDialog">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>

      <v-stepper v-model="step" dark style="background-color: transparent; border-radius: 28px">
        <v-stepper-header style="border-bottom: 1px solid #444">
          <v-stepper-step :complete="step > 1" step="1">Kündigungsgrund</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">Bestätigung</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3">E-Mail & Versand</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1" class="pa-4">
            <v-card-title class="h5" style="font-weight: 700; word-break: normal"> Vertrag kündigen </v-card-title>

            <div v-if="loading" class="text-center pa-10">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <p class="mt-4 body-1">Lade Vertragsdetails & Kündigungsgründe...</p>
            </div>

            <v-card-text v-if="activeContract && !loading">
              <p class="body-1">
                Du bist im Begriff, den Vertrag <strong>{{ activeContract.NAME }}</strong> für
                <strong>{{ user.vorname }} {{ user.nachname }}</strong> zu kündigen.
              </p>

              <v-alert :color="isLate ? 'warning' : 'info'" outlined icon="mdi-information-outline" class="mb-4">
                <div v-if="isLate">
                  <strong>Kündigung verspätet:</strong> Die Kündigungsfrist von 45 Tagen ist überschritten. Die Kündigung erfolgt auf das Ende der
                  <strong>nächsten</strong> Laufzeit.
                </div>
                <div v-else><strong>Korrekte Kündigung:</strong> Die Kündigung erfolgt fristgerecht.</div>
                <strong>Kündigungsdatum: {{ formattedTerminationDate }}</strong>
              </v-alert>

              <label class="font-weight-bold">Bitte Kündigungsgrund auswählen:</label>
              <v-radio-group v-model="selectedReasonId" dense>
                <v-radio v-for="reason in reasons" :key="reason.ReasonID" :label="reason.Name" :value="reason.ReasonID" color="primary"></v-radio>
              </v-radio-group>
              <v-alert v-if="!reasons.length" type="error" dense outlined> Es konnten keine Kündigungsgründe vom TAC-Server geladen werden. </v-alert>

              <v-alert v-if="isLate && !contractToTerminate && !loading" type="error" dense outlined>
                Kündigung verspätet, aber es konnte kein Folgevertrag im System gefunden werden, der gekündigt werden könnte. Bitte prüfe die
                TAC-Daten.
              </v-alert>
            </v-card-text>

            <v-card-actions v-if="!loading" class="pa-0 mt-4">
              <v-spacer></v-spacer>
              <v-btn text @click="closeDialog">Abbrechen</v-btn>
              <v-btn
                color="primary"
                large
                style="border-radius: 12px; font-weight: 700"
                :disabled="!selectedReasonId || loading || !reasons.length || !contractToTerminate"
                @click="step = 2">
                Weiter
              </v-btn>
            </v-card-actions>
          </v-stepper-content>

          <v-stepper-content step="2" class="pa-4">
            <div v-if="activeContract">
              <v-card-title class="h5" style="font-weight: 700">Bestätigung</v-card-title>
              <v-card-text>
                <p class="body-1">
                  Soll die Kündigung für <strong>{{ activeContract.NAME }}</strong> (Kunde: {{ user.vorname }} {{ user.nachname }}) zum
                  <strong>{{ formattedTerminationDate }}</strong>
                  jetzt definitiv im TAC-System erfasst werden?
                </p>
                <p>
                  Gewählter Grund: <strong>{{ selectedReasonName }}</strong>
                </p>
              </v-card-text>

              <v-card-actions class="pa-0 mt-4">
                <v-spacer></v-spacer>
                <v-btn text @click="step = 1">Zurück</v-btn>
                <v-btn color="primary" large style="border-radius: 12px; font-weight: 700" :loading="loading" @click="executeTermination">
                  In TAC kündigen
                </v-btn>
              </v-card-actions>
            </div>
          </v-stepper-content>

          <v-stepper-content step="3" class="pa-4">
            <v-card-title class="h5" style="font-weight: 700">
              <v-icon color="success" left>mdi-check-circle</v-icon>
              Kündigung erfolgreich!
            </v-card-title>
            <v-card-text>
              <p>Die Kündigung wurde im TAC erfasst. Bereite jetzt die Bestätigungs-E-Mail für den Kunden vor.</p>

              <v-text-field v-model="mail.to" label="An" readonly filled dense rounded></v-text-field>
              <v-text-field v-model="mail.subject" label="Betreff" filled dense rounded></v-text-field>
              <v-textarea v-model="mail.text" label="Nachricht" filled no-resize rows="10" rounded></v-textarea>

              <v-btn color="blue" text @click="previewPdf" :loading="pdfLoading">
                <v-icon left>mdi-file-eye-outline</v-icon>
                PDF-Vorschau anzeigen
              </v-btn>
            </v-card-text>

            <v-card-actions class="pa-0 mt-4">
              <v-spacer></v-spacer>
              <v-btn text @click="closeDialog">Schliessen (ohne E-Mail)</v-btn>
              <v-btn color="primary" large style="border-radius: 12px; font-weight: 700" :loading="mailLoading" @click="sendTerminationEmail">
                <v-icon left>mdi-send</v-icon>
                E-Mail jetzt senden
              </v-btn>
            </v-card-actions>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
// Importiere deine bestehenden API-Wrapper
import tacApi from "@/api/tacApi" // Pfad anpassen
import { format, addDays, addYears, isAfter } from "date-fns"
import api from "@/api"
export default {
  name: "TerminationDialog",
  props: {
    dialog: Boolean, // Gesteuert von aussen
    user: Object, // Das volle User-Objekt
    contractId: Number, // Die CONTRACTID des zu kündigenden Vertrags
  },
  data() {
    return {
      step: 1,
      loading: false,
      pdfLoading: false,
      mailLoading: false,
      activeContract: null, // Der Vertrag, der *angezeigt* wird (der aktuell laufende)
      contractToTerminate: null, // NEU: Der Vertrag, der *tatsächlich gekündigt* wird
      reasons: [],
      selectedReasonId: null,

      isLate: false,
      terminationDate: null,
      allUserContracts: [],
      mail: {
        to: "",
        subject: "Kündigungsbestätigung well come FIT",
        text: "",
      },
    }
  },

  computed: {
    selectedReasonName() {
      if (!this.reasons || this.reasons.length === 0) return "N/A"
      const reason = this.reasons.find((r) => r.ReasonID === this.selectedReasonId)
      return reason ? reason.Name : "N/A"
    },
    formattedTerminationDate() {
      if (!this.terminationDate) return ""
      return format(this.terminationDate, "dd.MM.yyyy")
    },
  },

  watch: {
    dialog(newVal) {
      if (newVal === true) {
        this.loadData()
      } else {
        this.reset()
      }
    },
  },

  methods: {
    async loadData() {
      if (!this.user || !this.contractId) return
      this.loading = true
      this.activeContract = null
      this.contractToTerminate = null // NEU

      try {
        const allContracts = await tacApi.fetchTACContractDetails(this.user._id)
        this.allUserContracts = allContracts
        // 1. Alle Vertragsdetails laden und speichern
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Uhrzeit ignorieren für sauberen Vergleich

        this.activeContract = allContracts.find((c) => {
          // Muss die richtige ID haben
          if (c.CONTRACTID !== this.contractId) return false

          const start = new Date(c.TERM_STARTDATE)
          const end = new Date(c.TERM_ENDDATE)

          // Auch hier Uhrzeiten nullen, um Probleme mit Zeitzonen zu vermeiden
          start.setHours(0, 0, 0, 0)
          end.setHours(0, 0, 0, 0)

          // Der Vertrag ist aktiv, wenn heute >= Start UND heute <= Ende
          return today >= start && today <= end
        })

        // Fallback: Falls "heute" zufällig genau eine Lücke zwischen zwei Laufzeiten ist
        // oder das Datum nicht greift, nehmen wir den mit dem aktuellsten Startdatum.
        if (!this.activeContract) {
          const contractsWithId = allContracts.filter((c) => c.CONTRACTID === this.contractId)

          // Sortiere absteigend nach Startdatum (neuester zuerst) und nimm den ersten
          contractsWithId.sort((a, b) => new Date(b.TERM_STARTDATE) - new Date(a.TERM_STARTDATE))

          if (contractsWithId.length > 0) {
            this.activeContract = contractsWithId[0]
          }
        }
        if (!this.activeContract) {
          alert("Fehler: Konnte keinen aktiven Vertrag zum Kündigen finden.")
          this.closeDialog()
          return
        }

        // 3. Kündigungs-Business-Logik anwenden
        // KORRIGIERT: Diese Methode setzt jetzt `this.isLate`, `this.terminationDate` UND `this.contractToTerminate`
        this.calculateTerminationDetails()

        // 4. Kündigungsgründe von TAC laden
        const response = await tacApi.getTACReasons(this.activeContract.RESSORTGROUPID, "MEMBERSHIP_TERMINATION")

        if (response && response.Reasons && response.Reasons.Reason) {
          this.reasons = response.Reasons.Reason.map((r) => {
            return {
              ReasonID: parseInt(r.ReasonID.UniqueID[0].value, 10),
              Name: r.Reasons.TextElement.find((t) => t.language === "de")?.value || r.Reasons.TextElement[0]?.value || r.ReasonCode,
            }
          })
        } else {
          console.error("Konnte Kündigungsgründe nicht parsen:", response)
          this.reasons = []
        }
      } catch (err) {
        console.error("Fehler beim Laden der Vertragsdaten oder Gründe:", err)
        alert("Fehler beim Laden der Vertragsdaten.")
        this.reasons = []
      } finally {
        this.loading = false
      }
    },

    // KORRIGIERT: Umbenannt von calculateTerminationDate zu calculateTerminationDetails
    calculateTerminationDetails() {
      const CANCELLATION_PERIOD_DAYS = 45
      const today = new Date()

      // Das Datum, an dem die *aktuelle* Laufzeit endet
      const currentTermEnd = new Date(this.activeContract.TERM_ENDDATE)
      console.log(this.activeContract)
      // Das letztmögliche Kündigungsdatum (45 Tage vor Ende)
      const deadline = addDays(currentTermEnd, -CANCELLATION_PERIOD_DAYS)

      // Prüfen, ob "heute" nach dem Stichtag liegt
      this.isLate = isAfter(today, deadline)

      if (this.isLate) {
        // --- ZU SPÄT ---
        // Wir müssen den *Folgevertrag* finden.
        // Das ist der Vertrag mit derselben CONTRACTID, dessen TERM_STARTDATE *nach* dem TERM_ENDDATE des aktuellen Vertrags liegt.

        const nextContract = this.allUserContracts
          .filter((c) => {
            if (c.CONTRACTTYP === "Hauptvertrag") return c.CONTRACTTYP === "Hauptvertrag" && new Date(c.TERM_STARTDATE) > currentTermEnd // Startet in der Zukunft
          })
          .sort((a, b) => new Date(a.TERM_STARTDATE) - new Date(b.TERM_STARTDATE))[0] // Nimm den frühesten Zukunftsvertrag

        if (nextContract) {
          // Wir haben den Folgevertrag gefunden!
          this.contractToTerminate = nextContract
          this.terminationDate = new Date(nextContract.TERM_ENDDATE) // Das ist das korrekte Kündigungsdatum
        } else {
          // Problem: Kündigung ist zu spät, aber es gibt keinen Folgevertrag im Array.
          // Das sollte nicht passieren, wenn die Verlängerung schon im TAC ist.
          // Wir setzen `contractToTerminate` auf null, der "Weiter"-Button wird deaktiviert.
          console.error(
            "Kündigung verspätet, aber kein Folgevertrag (mit passender CONTRACTID und zukünftigem TERM_STARTDATE) in allUserContracts gefunden."
          )
          this.contractToTerminate = null
          // Wir setzen trotzdem ein "theoretisches" Datum für die Anzeige
          let years = 1
          if (this.activeContract.NAME.includes("2 Jahr")) years = 2
          if (this.activeContract.NAME.includes("3 Jahr")) years = 3
          this.terminationDate = addYears(currentTermEnd, years)
        }
      } else {
        // --- FRISTGERECHT ---
        // Kündigung auf Ende der AKTUELLEN Laufzeit.
        this.terminationDate = currentTermEnd
        // Wir kündigen den *aktuellen* Vertrag.
        this.contractToTerminate = this.activeContract
      }

      // E-Mail-Text erst *nach* finaler Datums- und Vertragsfindung setzen
      if (this.isLate) {
        this.mail.text = this.getLateEmailHtml()
      } else {
        this.mail.text = this.getRegularEmailHtml()
      }
      this.mail.to = this.user.email
    },

    async executeTermination() {
      // NEU: Guard, falls kein Vertrag zum Kündigen gefunden wurde (wichtig für den "isLate"-Fall)
      if (!this.contractToTerminate) {
        alert("Fehler: Es konnte nicht ermittelt werden, welcher Vertrag gekündigt werden soll. (Evtl. fehlt der Folgevertrag im System)")
        return
      }

      this.loading = true
      try {
        const data = {
          // KORRIGIERT: Wir verwenden die IDs des `contractToTerminate`
          ResortId: this.contractToTerminate.RESSORTGROUPID,
          ContractId: this.contractToTerminate.CONTRACTID,
          ReasonId: this.selectedReasonId,
          ReasonType: "MEMBERSHIP_TERMINATION",
          CheckTermination: false,
        }

        // 5. TAC API Aufruf
        const response = await tacApi.terminateTACContract(data)

        if (response && response.Result && response.Result.resultStatusFlag === "SUCCESS") {
          this.step = 3 // Weiter zum E-Mail-Schritt
        } else {
          throw new Error(response.Result.Text ? response.Result.Text[0].value : "Unbekannter TAC-Fehler")
        }
      } catch (err) {
        console.error("Fehler bei der TAC-Kündigung:", err)
        alert(`Kündigung fehlgeschlagen: ${err.message}`)
      } finally {
        this.loading = false
      }
    },

    async getPdfBlob() {
      this.pdfLoading = true
      try {
        let contractsToDisplay = []

        // NEUE LOGIK:
        // Prüfen, ob der gekündigte Vertrag ein Hauptvertrag ist
        if (this.activeContract.CONTRACTTYP === "Hauptvertrag") {
          // FALL 1: Hauptvertrag wird gekündigt
          // Suche alle Verträge (Haupt + Zusatz), die zur selben Laufzeit gehören
          console.log("PDF: Hauptvertrag wird gekündigt. Suche zugehörige Pakete...")
          contractsToDisplay = this.allUserContracts.filter(
            (c) =>
              c.TERM_STARTDATE === this.activeContract.TERM_STARTDATE &&
              c.TERM_ENDDATE === this.activeContract.TERM_ENDDATE &&
              (c.CONTRACTTYP === "Hauptvertrag" ||
                c.CONTRACTTYP === "Zusatzvertrag" ||
                (c.CONTRACTTYP === "Bonusvertrag" && c.GROUP_NAME === "Zusatzpakete"))
          )
        } else {
          // FALL 2: Nur ein Zusatzpaket wird gekündigt
          // Zeige NUR dieses eine Paket im PDF an.
          console.log("PDF: Nur Zusatzpaket wird gekündigt.")
          contractsToDisplay = [this.activeContract]
        }

        if (contractsToDisplay.length === 0) {
          // Fallback, falls etwas schiefgeht
          contractsToDisplay = [this.activeContract]
        }

        // 6. PDF auf dem Server generieren lassen
        const pdfBlob = await api.generateTerminationPdf({
          user: this.user,
          contractsToDisplay: contractsToDisplay, // Sende die korrekte Liste
          terminationDate: this.formattedTerminationDate,
          isLate: this.isLate,
        })

        return pdfBlob
      } catch (err) {
        console.error("Fehler bei PDF-Vorschau:", err)
        alert("PDF konnte nicht erstellt werden.")
        return null
      } finally {
        this.pdfLoading = false
      }
    },

    async previewPdf() {
      const blob = await this.getPdfBlob()
      if (blob) {
        const fileURL = URL.createObjectURL(blob)
        window.open(fileURL)
      }
    },
    async sendTerminationEmail() {
      this.mailLoading = true
      try {
        const pdfBlob = await this.getPdfBlob()
        if (!pdfBlob) throw new Error("PDF-Generierung fehlgeschlagen.")

        const pdfBase64 = await this.blobToBase64(pdfBlob)

        const htmlBody = this.mail.text.replace(/\n/g, "<br>")

        const mailData = {
          to: this.mail.to,
          subject: this.mail.subject,
          html: htmlBody,
          attachments: [
            {
              filename: "Kündigungsbestätigung.pdf",
              content: pdfBase64.split(",")[1],
              encoding: "base64",
              contentType: "application/pdf",
            },
          ],
        }

        await api.sendTerminationMail(mailData)

        alert("E-Mail erfolgreich versendet!")
        this.closeDialog()
      } catch (err) {
        console.error("Fehler beim E-Mail-Versand:", err)
        alert(`E-Mail-Versand fehlgeschlagen: ${err.message}`)
      } finally {
        this.mailLoading = false
      }
    },
    // Hilfsfunktionen
    getRegularEmailHtml() {
      const contractName = this.activeContract ? this.activeContract.NAME : "[Vertrag]"
      return `Hallo ${this.user.vorname}\n\nAnbei senden wir dir die Kündigungsbestätigung für deinen Vertrag.\n\nWir bedauern deinen Entscheid. Wir sind stets daran, uns zu verbessern. Dürfen wir dich bitten, uns den Grund für Deine Kündigung mitzuteilen?\n\nSportliche Grüsse\nwell come FIT AG`
    },
    getLateEmailHtml() {
      // Dein neuer Text wird hier verwendet und formatiert
      return (
        `Hallo ${this.user.vorname}\n\n` +
        `Gemäss unseren Mitgliedschaftsbedingungen verlängert sich die Mitgliedschaft automatisch, wenn diese nicht fristgerecht und schriftlich (spätestens 45 Tage vor Vertragsablauf) gekündigt wird (siehe Punkt 2 der Mitgliedschaftsbedingungen auf dem Vertrag).\n\n` +
        `Die Kündigungsfrist in Deinem Fall ist bereits abgelaufen. Der Vertrag lässt sich nur per Vertragsablauf kündigen. Anbei senden wir dir die Kündigungsbestätigung per ${this.formattedTerminationDate}.\n\n` +
        `Bitte beachte, dass Dein Mitgliedschaftsbeitrag somit auf Abonnementbeginn fällig ist.\n\n` +
        `Folgende Varianten können wir dir anbieten:\n\n` +
        `  •  Zahlung des Mitgliedschaftsbeitrages in Raten\n` +
        `     (2 oder 12 Raten - dafür benötigen wir einen neuen Vertrag, es wird ein Ratenzuschlag erhoben)\n\n` +
        `  •  Time-Stop\n` +
        `     (Unterbruch des Abonnements - gebührenpflichtig)\n\n` +
        `  •  Übergabe der Mitgliedschaft an ein Nichtmitglied (mit unterzeichneter, schriftlicher Bestätigung)\n` +
        `     (Umschreibe- und Einschreibegebühren total CHF 119.00)\n\n` +
        `Wir danken dir für deine Kenntnisnahme.\n\n` +
        `Sportliche Grüsse\n` +
        `well come FIT AG`
      )
    },
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = reject
        reader.onload = () => {
          resolve(reader.result)
        }
        reader.readAsDataURL(blob)
      })
    },
    closeDialog() {
      this.$emit("close")
    },
    reset() {
      this.step = 1
      this.loading = false
      this.mailLoading = false
      this.pdfLoading = false
      this.activeContract = null
      this.contractToTerminate = null // NEU
      this.reasons = []
      this.selectedReasonId = null
      this.isLate = false
      this.terminationDate = null
    },
  },
}
</script>

<style scoped>
/* TikTok-Style Anpassungen */
.termination-dialog {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}
.v-stepper {
  box-shadow: none !important;
}
.v-stepper__header {
  border-radius: 28px 28px 0 0;
  border-bottom: 1px solid #333;
}
.v-stepper__content {
  padding: 24px !important;
}
</style>
